import { ref } from "@vue/composition-api";
import store from "@/store";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const EXCEL_EXTENSIONS = [".xls", ".xlsx"];

const isExcelFile = (file) => {
  const name = file.name.toLowerCase();
  return EXCEL_EXTENSIONS.some((ext) => name.endsWith(ext));
};

export const normalizeImportResponse = (data) => {
  if (!data) {
    return {
      type: "error",
      message: "Something went wrong while importing user list",
    };
  }

  if (data.success === 100 || data.success === "100") {
    return {
      type: "success",
      message: data.message,
      processedTotal: data.processedTotal,
      createdCount: data.createdCount,
      updatedCount: data.updatedCount,
      softDeletedCount: data.softDeletedCount,
      skippedCount: data.skippedCount,
      errors: data.errors || [],
    };
  }

  return {
    type: "error",
    message: data.message || "Import failed",
    code: data.code,
    headerRowNumber: data.headerRowNumber,
    sampleRowsChecked: data.sampleRowsChecked,
    detectedHeaders: data.detectedHeaders || null,
    validationErrors: data.validationErrors || [],
  };
};

export default function useImportUsersExcel({ onSuccess } = {}) {
  const toast = useToast();
  const selectedFile = ref(null);
  const isImporting = ref(false);
  const isImportResultModalActive = ref(false);
  const importResult = ref(null);

  const closeImportResultModal = () => {
    isImportResultModalActive.value = false;
    importResult.value = null;
  };

  const uploadExcelFile = (file) => {
    isImporting.value = true;
    isImportResultModalActive.value = true;
    importResult.value = null;

    store
      .dispatch("apps-members/importUserList", file)
      .then((response) => {
        const result = normalizeImportResponse(response.data);
        importResult.value = result;
        if (result.type === "success" && onSuccess) {
          onSuccess();
        }
      })
      .catch((error) => {
        importResult.value = normalizeImportResponse(
          error?.response?.data || {
            message:
              error?.message ||
              "Something went wrong while importing user list",
          }
        );
      })
      .finally(() => {
        isImporting.value = false;
        selectedFile.value = null;
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isExcelFile(file)) {
      toast({
        component: ToastificationContent,
        props: {
          title: "Please select a valid Excel file (.xls or .xlsx)",
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
      });
      selectedFile.value = null;
      event.target.value = "";
      return;
    }

    uploadExcelFile(file);
    event.target.value = "";
  };

  return {
    selectedFile,
    handleFileChange,
    isImporting,
    isImportResultModalActive,
    importResult,
    closeImportResultModal,
  };
};
